import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Monitor, Smartphone, Globe, CheckCircle2, Download, Apple, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

// Download Links
const DOWNLOAD_LINKS = {
  desktop: 'https://download.terminal.free/cdn/web/td.capital.ltd/mt5/tdcapital5setup.exe',
  ios: 'https://download.terminal.free/cdn/mobile/mt5/ios?server=TDCapital-Server',
  android: 'https://download.terminal.free/cdn/mobile/mt5/android?server=TDCapital-Server',
};

const platforms = [
  {
    icon: Monitor,
    title: 'MetaTrader 5',
    subtitle: 'Desktop Trading',
    description: 'The world\'s leading trading platform with advanced charting, algorithmic trading, and professional tools.',
    features: [
      'Advanced charting with 100+ indicators',
      'Automated trading with Expert Advisors (EAs)',
      'One-click trading and hedging',
      'Multi-asset class support',
      'Economic calendar and market news',
    ],
    image: 'https://images.unsplash.com/photo-1629963918958-1b62cfe3fe92?w=800&h=600&fit=crop',
    downloadType: 'desktop',
  },
  {
    icon: Smartphone,
    title: 'Mobile Trading',
    subtitle: 'iOS & Android',
    description: 'Trade on the go with our powerful mobile apps. Full MT5 functionality in your pocket.',
    features: [
      'Full MT5 mobile experience',
      'Real-time quotes and charts',
      'One-tap order execution',
      'Push notifications for market events',
      'Biometric authentication',
    ],
    image: 'https://images.unsplash.com/photo-1633431871820-ca72e0da2e2b?w=800&h=600&fit=crop',
    downloadType: 'mobile',
  },
  {
    icon: Globe,
    title: 'WebTrader',
    subtitle: 'Browser-Based',
    description: 'No downloads required. Trade directly from your browser with our intuitive web platform.',
    features: [
      'Access from any device',
      'No installation needed',
      'TradingView charts integrated',
      'Fast order execution',
      'Cloud-based watchlists',
    ],
    image: 'https://images.unsplash.com/photo-1635236190542-d43e4d4b9e4b?w=800&h=600&fit=crop',
    downloadType: 'web',
  },
];

export const PlatformsSection = () => {
  const handleDownload = (link) => {
    window.open(link, '_blank');
  };

  const renderDownloadButtons = (platform) => {
    if (platform.downloadType === 'desktop') {
      return (
        <Button 
          className="w-full btn-premium text-white font-semibold"
          onClick={() => handleDownload(DOWNLOAD_LINKS.desktop)}
          data-testid="platform-download-desktop"
        >
          <Download className="mr-2 h-5 w-5" />
          Download for Windows
        </Button>
      );
    }
    
    if (platform.downloadType === 'mobile') {
      return (
        <div className="flex gap-2">
          <Button 
            className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-semibold"
            onClick={() => handleDownload(DOWNLOAD_LINKS.ios)}
            data-testid="platform-download-ios"
          >
            <Apple className="mr-1 h-4 w-4" />
            iOS
          </Button>
          <Button 
            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
            onClick={() => handleDownload(DOWNLOAD_LINKS.android)}
            data-testid="platform-download-android"
          >
            <Smartphone className="mr-1 h-4 w-4" />
            Android
          </Button>
        </div>
      );
    }
    
    // WebTrader - link to web platform page
    return (
      <Link to="/platforms/web" className="block">
        <Button className="w-full btn-premium text-white font-semibold">
          <ExternalLink className="mr-2 h-5 w-5" />
          Launch WebTrader
        </Button>
      </Link>
    );
  };

  return (
    <section id="platforms" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Trade on Any Device
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Powerful platforms designed for professional traders. MetaTrader 5, Mobile, and Web.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {platforms.map((platform, index) => (
            <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col">
              {/* Platform Image */}
              <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800">
                <img
                  src={platform.image}
                  alt={platform.title}
                  className="w-full h-full object-cover opacity-70 mix-blend-luminosity hover:opacity-90 hover:mix-blend-normal transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                    <platform.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Platform Content */}
              <div className="p-6 flex flex-col flex-1">
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{platform.title}</h3>
                  <p className="text-sm text-gray-500 font-semibold uppercase tracking-wide">{platform.subtitle}</p>
                </div>
                <p className="text-gray-600 mb-6">{platform.description}</p>

                {/* Features List */}
                <div className="space-y-3 mb-6">
                  {platform.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto">
                  {renderDownloadButtons(platform)}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* MT5 Highlight Banner */}
        <div className="mt-16 max-w-5xl mx-auto">
          <Card className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex-1">
                <h3 className="text-3xl font-bold mb-4">Why MetaTrader 5?</h3>
                <p className="text-gray-300 text-lg mb-6">
                  MetaTrader 5 is the world's most popular multi-asset trading platform. Used by millions of traders worldwide, MT5 offers superior charting, algorithmic trading, and a marketplace of thousands of trading robots and indicators.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-primary">38+</div>
                    <div className="text-sm text-gray-400">Indicators</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-primary">9</div>
                    <div className="text-sm text-gray-400">Timeframes</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-primary">21</div>
                    <div className="text-sm text-gray-400">Objects</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-primary">∞</div>
                    <div className="text-sm text-gray-400">Possibilities</div>
                  </div>
                </div>
              </div>
              <div>
                <Button size="lg" variant="outline" className="bg-white text-gray-900 hover:bg-gray-100 font-semibold text-base px-8">
                  Learn More About MT5
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default PlatformsSection;